import { buildCartLandingShopperKey } from "@/utils/cart/cartLandingShopper"

const STORAGE_KEY = "kohler.cartLandingViewModel.v1"
const VIEW_MODEL_VERSION = 5
const DEFAULT_LOCALE = "en"
const MAX_AGE_MS = 24 * 60 * 60 * 1000
const MAX_VIEW_MODEL_LENGTH = 512 * 1024

let pendingBuildTimer = null
let pendingBuildResolve = null
let buildGeneration = 0
let memoryCache = null
let inflightBuild = null
let inflightBuildKey = ""

const normalizeLocale = locale => {
  const normalized = String(locale || "")
    .trim()
    .toLowerCase()
  return normalized || DEFAULT_LOCALE
}

export const getCartLandingLocale = (currentLocale = "") => {
  if (currentLocale) return normalizeLocale(currentLocale)
  if (typeof window === "undefined") return DEFAULT_LOCALE

  const nextData = window.__NEXT_DATA__ || {}
  const pathLocale = window.location.pathname?.split("/")[1] || ""
  const locales =
    Array.isArray(nextData.locales) && nextData.locales.length
      ? nextData.locales
      : []
  if (Array.isArray(locales) && locales.includes(pathLocale)) {
    return normalizeLocale(pathLocale)
  }

  if (Array.isArray(locales) && locales.length) {
    return normalizeLocale(nextData.defaultLocale || DEFAULT_LOCALE)
  }

  return normalizeLocale(nextData.locale)
}

const getStorage = () => {
  if (typeof window === "undefined") return null
  try {
    return window.localStorage
  } catch (err) {
    console.warn("Cart landing view-model storage is unavailable:", err)
    return null
  }
}

export const getCartLandingShopperKeyFromStorage = (storage = getStorage()) => {
  if (!storage) return ""

  try {
    const rawAuth = storage.getItem("okta-token-storage")
    if (rawAuth && rawAuth !== "{}") {
      const auth = JSON.parse(rawAuth)
      const claims = auth?.accessToken?.claims || auth?.user || {}
      const customerId = claims.commercetoolsID || claims.commercetoolsId || ""
      if (customerId) {
        return buildCartLandingShopperKey({ isAuth: true, customerId })
      }
    }

    const anonymousId = storage.getItem("anonymousId") || ""
    return buildCartLandingShopperKey({ anonymousId })
  } catch (err) {
    console.warn("Failed to resolve cart landing view-model shopper:", err)
    return ""
  }
}

const getCartShopperKey = cart => {
  const customerId =
    (typeof cart?.customerId === "string" && cart.customerId) ||
    cart?.customerId?.id ||
    cart?.customer?.id ||
    ""
  if (customerId) {
    return buildCartLandingShopperKey({ isAuth: true, customerId })
  }
  return buildCartLandingShopperKey({
    anonymousId: cart?.anonymousId || "",
  })
}

const cancelPendingBuildTimer = () => {
  const resolvePendingBuild = pendingBuildResolve
  if (pendingBuildTimer && typeof window !== "undefined") {
    try {
      window.clearTimeout(pendingBuildTimer)
    } catch (err) {
      console.warn(
        "Failed to cancel pending cart landing view-model build:",
        err
      )
    }
  }
  pendingBuildTimer = null
  pendingBuildResolve = null
  resolvePendingBuild?.(null)
}

const invalidateInflightBuild = () => {
  buildGeneration += 1
  cancelPendingBuildTimer()
  inflightBuild = null
  inflightBuildKey = ""
}

const removeViewModelStorage = storage => {
  if (!storage) return
  try {
    storage.removeItem(STORAGE_KEY)
  } catch (err) {
    console.warn("Failed to clear cart landing view-model:", err)
  }
}

const setMemoryCache = ({
  cartId,
  cartVersion,
  shopperKey,
  locale,
  cartFingerprint,
  viewModel,
  buildKey = "",
}) => {
  memoryCache = {
    cartId,
    cartVersion:
      typeof cartVersion === "number" ? cartVersion : Number(cartVersion) || 0,
    shopperKey,
    locale: normalizeLocale(locale),
    cartFingerprint,
    buildKey,
    viewModel,
    savedAt: Date.now(),
  }
}

const isMemoryNewerOrEqual = (cartId, cartVersion, locale) => {
  if (
    !memoryCache ||
    memoryCache.cartId !== cartId ||
    memoryCache.locale !== normalizeLocale(locale)
  ) {
    return false
  }
  const nextVersion =
    typeof cartVersion === "number" ? cartVersion : Number(cartVersion) || 0
  return memoryCache.cartVersion >= nextVersion
}

export const clearCartLandingViewModel = () => {
  invalidateInflightBuild()
  memoryCache = null
  removeViewModelStorage(getStorage())
}

const CART_ITEM_CACHE_FIELDS = [
  "isBundleParent",
  "isBundleChildItem",
  "id",
  "bundleParentId",
  "parentBundleSku",
  "sku",
  "customerFacingSKU",
  "productRoom",
  "slug",
  "quantity",
  "image",
  "category",
  "brand",
  "currencySign",
  "name",
  "desc",
  "color",
  "size",
  "shape",
  "unitPrice",
  "totalPrice",
  "discountUnitPrice",
  "discountPrice",
  "discountedPricePerQuantity",
  "discountedPriceProductLevel",
  "discountPercent",
  "discountAmount",
  "isComboSku",
  "backOrder",
  "businessUnitName",
  "productCollectionsName",
  "addedAt",
  "availableQty",
  "outOfStock",
  "noShipping",
  "leadTime",
  "bundleItemsCount",
  "zipCode",
]

const cloneAllowedValue = value => {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value
  }

  if (Array.isArray(value)) {
    return value
      .map(cloneAllowedValue)
      .filter(clonedValue => clonedValue !== undefined)
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((result, [key, nestedValue]) => {
      if (["__proto__", "constructor", "prototype"].includes(key)) {
        return result
      }
      const clonedValue = cloneAllowedValue(nestedValue)
      if (clonedValue !== undefined) result[key] = clonedValue
      return result
    }, {})
  }

  return undefined
}

const sanitizeDiscounts = discounts =>
  Array.isArray(discounts)
    ? discounts.map(discount => ({
        type: discount?.type || "",
        percent: discount?.percent ?? 0,
        absolute: discount?.absolute ?? 0,
        isAbs: Boolean(discount?.isAbs),
        isRel: Boolean(discount?.isRel),
        code: discount?.code || "",
      }))
    : []

const sanitizeCartItem = item => {
  if (!item || typeof item !== "object") return null

  const safeItem = {}
  CART_ITEM_CACHE_FIELDS.forEach(field => {
    if (item[field] !== undefined)
      safeItem[field] = cloneAllowedValue(item[field])
  })

  if (Array.isArray(item.bundleLineItems)) {
    safeItem.bundleLineItems = item.bundleLineItems
      .map(sanitizeCartItem)
      .filter(Boolean)
  }
  if (Array.isArray(item.discountsList)) {
    safeItem.discountsList = sanitizeDiscounts(item.discountsList)
  }

  return safeItem
}

const sanitizeCartItemList = items =>
  Array.isArray(items) ? items.map(sanitizeCartItem).filter(Boolean) : []

const sanitizePromoCodes = codes =>
  Array.isArray(codes)
    ? codes.map(code => ({
        code: code?.code || "",
        discount: code?.discount ?? "",
        id: code?.id || "",
        type: code?.type || "",
        name: code?.name || "",
      }))
    : []

const sanitizeOldDiscountCodes = codes =>
  Array.isArray(codes)
    ? codes.map(code => ({
        state: code?.state || "",
        discountCode: {
          obj: {
            code: code?.discountCode?.obj?.code || "",
          },
        },
      }))
    : []

const sanitizeShippingTotals = totals =>
  Array.isArray(totals)
    ? totals.map(item => ({
        name: item?.name || "",
        total: item?.total ?? 0,
      }))
    : []

const pickViewModelForStorage = viewModel => {
  if (!viewModel || typeof viewModel !== "object") return null
  const cartItems = sanitizeCartItemList(viewModel.cartItems)
  if (cartItems.length === 0) return null

  return {
    cartItems,
    outOfStockItems: sanitizeCartItemList(viewModel.outOfStockItems),
    noShippingItems: sanitizeCartItemList(viewModel.noShippingItems),
    subtotal: viewModel.subtotal,
    subtotalWithDiscount: viewModel.subtotalWithDiscount,
    totalPromotions: viewModel.totalPromotions,
    estimatedTax: viewModel.estimatedTax,
    estimatedShipping: viewModel.estimatedShipping,
    estimatedTotal: viewModel.estimatedTotal,
    shippingTotal: viewModel.shippingTotal,
    promoCodes: sanitizePromoCodes(viewModel.promoCodes),
    oldPromoCodes: sanitizePromoCodes(viewModel.oldPromoCodes),
    oldDiscountCodes: sanitizeOldDiscountCodes(viewModel.oldDiscountCodes),
    shippingAllTotal: sanitizeShippingTotals(viewModel.shippingAllTotal),
    freightDiscountTotal: viewModel.freightDiscountTotal,
    specFiles: Array.isArray(viewModel.specFiles)
      ? viewModel.specFiles.filter(file => typeof file === "string")
      : [],
  }
}

const resolveShopperKey = (shopperKey, cart) => {
  const storageKey = getCartLandingShopperKeyFromStorage()
  const cartKey = cart ? getCartShopperKey(cart) : ""
  const key = shopperKey || cartKey || storageKey
  if (!key || !storageKey || key !== storageKey) return ""
  if (cartKey && cartKey !== storageKey) return ""
  return key
}

const getLineItemFingerprint = (lineItems = []) =>
  lineItems
    .map(item =>
      [
        item?.id || "",
        item?.productId || "",
        item?.variant?.sku || item?.sku || "",
        item?.quantity ?? "",
        item?.totalPrice?.centAmount ?? "",
        item?.price?.value?.centAmount ?? "",
      ].join("~")
    )
    .join("|")

export const getCartLandingCartFingerprint = cart => {
  if (!cart?.id) return ""
  return [
    cart.id,
    cart.version ?? "",
    cart.lineItems?.length || 0,
    getLineItemFingerprint(cart.lineItems),
    cart.totalPrice?.centAmount ?? cart.estimatedTotal?.centAmount ?? "",
    cart.shippingTotal?.centAmount ?? "",
  ].join(":")
}

const getDerivedInputFingerprint = ({
  shippingItemstoCheckout = [],
  noShippingItems = [],
  quantityUpdatedFlag = false,
} = {}) =>
  JSON.stringify({
    shippingItemstoCheckout: (Array.isArray(shippingItemstoCheckout)
      ? shippingItemstoCheckout
      : []
    ).map(item => ({
      warningMessage: item?.warningMessage || "",
      parentLineItemId: item?.parentLineItemId || "",
      lineItemId: item?.lineItemId || "",
    })),
    noShippingItems: (Array.isArray(noShippingItems) ? noShippingItems : [])
      .map(item =>
        typeof item === "string" ? item : item?.sku || item?.id || ""
      )
      .sort(),
    quantityUpdatedFlag: quantityUpdatedFlag || "",
  })

/**
 * Stable cart response identity. Includes quantities/prices so version-less or
 * same-count updates still rebuild. Derived inputs are intentionally separate
 * in getCartLandingBuildKey so cached UI can paint while they revalidate.
 * @param {Object} cart Cart response.
 * @param {Object} [options] Derived inputs and locale.
 * @return {string} Stable build key.
 */
export const getCartLandingBuildKey = (cart, options = {}) => {
  const cartFingerprint = getCartLandingCartFingerprint(cart)
  if (!cartFingerprint) return ""
  return `${normalizeLocale(
    options.locale
  )}:${cartFingerprint}:${getDerivedInputFingerprint(options)}`
}

/**
 * Persist a derived cart landing view-model (memory + localStorage).
 * Refuses to write when shopper identity cannot be confirmed, or when a
 * newer version for the same cart is already cached.
 * @param {Object} viewModel Derived cart landing data.
 * @param {Object} [options] Cart identity and cache metadata.
 * @return {boolean} Whether the view-model was persisted.
 */
export const saveCartLandingViewModel = (
  viewModel,
  {
    cartId = "",
    cartVersion = 0,
    shopperKey = "",
    cart = null,
    locale = "",
    cartFingerprint = "",
    buildKey = "",
  } = {}
) => {
  const safeViewModel = pickViewModelForStorage(viewModel)
  if (!safeViewModel || !cartId) {
    clearCartLandingViewModel()
    return false
  }

  const key = resolveShopperKey(shopperKey, cart)
  if (!key) return false

  const resolvedCartFingerprint =
    cartFingerprint || (cart ? getCartLandingCartFingerprint(cart) : "")
  if (!resolvedCartFingerprint) return false

  const resolvedLocale = normalizeLocale(locale || getCartLandingLocale())

  const version =
    typeof cartVersion === "number" ? cartVersion : Number(cartVersion) || 0
  if (
    isMemoryNewerOrEqual(cartId, version, resolvedLocale) &&
    memoryCache?.shopperKey === key
  ) {
    // Same or newer cache already present for this shopper/cart.
    if (memoryCache.cartVersion > version) return false
  }

  const resolvedBuildKey =
    buildKey ||
    (cart ? getCartLandingBuildKey(cart, { locale: resolvedLocale }) : "")

  setMemoryCache({
    cartId,
    cartVersion: version,
    shopperKey: key,
    locale: resolvedLocale,
    cartFingerprint: resolvedCartFingerprint,
    viewModel: safeViewModel,
    buildKey: resolvedBuildKey,
  })

  const storage = getStorage()
  if (!storage) return true

  const payload = {
    version: VIEW_MODEL_VERSION,
    savedAt: Date.now(),
    shopperKey: key,
    cartId,
    cartVersion: version,
    locale: resolvedLocale,
    cartFingerprint: resolvedCartFingerprint,
    buildKey: resolvedBuildKey,
    viewModel: safeViewModel,
  }

  try {
    const serialized = JSON.stringify(payload)
    if (serialized.length > MAX_VIEW_MODEL_LENGTH) {
      removeViewModelStorage(storage)
      return true
    }
    storage.setItem(STORAGE_KEY, serialized)
    return true
  } catch (err) {
    console.warn("Failed to save cart landing view-model:", err)
    removeViewModelStorage(storage)
    return true
  }
}

/**
 * When CT cart response changes, immediately start building the derived
 * landing view-model so the (often one-time) /cart visit can paint instantly.
 * Reuses an in-flight build for the same cart fingerprint.
 * @param {Object} cart Cart response.
 * @param {string} [shopperKey] Shopper cache identity.
 * @param {Object} [options] Derived inputs and locale.
 * @return {boolean} Whether a build was scheduled or already available.
 */
export const scheduleBuildCartLandingViewModel = (
  cart,
  shopperKey = "",
  {
    locale = "",
    shippingItemstoCheckout = [],
    noShippingItems = [],
    quantityUpdatedFlag = false,
  } = {}
) => {
  if (typeof window === "undefined") return false
  if (
    !cart?.id ||
    !Array.isArray(cart.lineItems) ||
    cart.lineItems.length === 0
  ) {
    clearCartLandingViewModel()
    return false
  }

  const key = resolveShopperKey(shopperKey, cart)
  if (!key) return false

  const resolvedLocale = normalizeLocale(locale || getCartLandingLocale())

  const buildKey = getCartLandingBuildKey(cart, {
    locale: resolvedLocale,
    shippingItemstoCheckout,
    noShippingItems,
    quantityUpdatedFlag,
  })

  const cartFingerprint = getCartLandingCartFingerprint(cart)
  const cached = readCartLandingViewModel({
    cartId: cart.id,
    shopperKey: key,
    locale: resolvedLocale,
    cartFingerprint,
    buildKey,
  })
  if (cached) {
    invalidateInflightBuild()
    return true
  }

  if (inflightBuild && inflightBuildKey === buildKey) {
    return true
  }

  const paintCache = readCartLandingViewModel({
    cartId: cart.id,
    shopperKey: key,
    locale: resolvedLocale,
    cartFingerprint,
  })
  if (paintCache) {
    // Keep same-cart UI available while derived inputs rebuild.
    invalidateInflightBuild()
  } else {
    // A changed cart/context invalidates the old model before rebuilding.
    clearCartLandingViewModel()
  }
  const generation = buildGeneration
  const cartId = cart.id
  const cartVersion = cart.version ?? 0

  const build = async () => {
    if (generation !== buildGeneration) return null
    pendingBuildTimer = null

    try {
      const { calculateCartData } = await import("@/utils/helper")
      const viewModel = await calculateCartData(
        {
          cart,
          shippingItemstoCheckout,
          noShippingItems,
          quantityUpdatedFlag,
        },
        true
      )
      if (generation !== buildGeneration) return null

      const saved = saveCartLandingViewModel(viewModel, {
        cartId,
        cartVersion,
        shopperKey: key,
        cart,
        locale: resolvedLocale,
        buildKey,
      })
      if (!saved || generation !== buildGeneration) return null

      const cached = readCartLandingViewModel({
        cartId,
        shopperKey: key,
        locale: resolvedLocale,
        buildKey,
      })
      if (!cached) return null

      return {
        ...cached,
        analyticsCart: viewModel?.cart || null,
      }
    } catch (err) {
      console.warn("Failed to build cart landing view-model:", err)
      return null
    } finally {
      if (generation === buildGeneration) {
        inflightBuild = null
        inflightBuildKey = ""
      }
    }
  }

  const buildPromise = new Promise(resolve => {
    pendingBuildResolve = resolve
    pendingBuildTimer = window.setTimeout(() => {
      pendingBuildResolve = null
      resolve(build())
    }, 0)
  })
  inflightBuild = buildPromise
  inflightBuildKey = buildKey

  return true
}

export const getInflightCartLandingViewModel = () => inflightBuild

export const getInflightCartLandingBuildKey = () => inflightBuildKey

export const readCartLandingViewModel = ({
  cartId = "",
  shopperKey = "",
  locale = "",
  cartFingerprint = "",
  buildKey = "",
} = {}) => {
  const currentShopperKey = shopperKey || getCartLandingShopperKeyFromStorage()
  if (!currentShopperKey) return null
  const resolvedLocale = normalizeLocale(locale || getCartLandingLocale())

  if (
    memoryCache?.viewModel?.cartItems?.length &&
    memoryCache.shopperKey === currentShopperKey &&
    memoryCache.locale === resolvedLocale &&
    (!cartId || memoryCache.cartId === cartId) &&
    (!cartFingerprint || memoryCache.cartFingerprint === cartFingerprint) &&
    (!buildKey || memoryCache.buildKey === buildKey)
  ) {
    const age = Date.now() - memoryCache.savedAt
    if (age >= 0 && age <= MAX_AGE_MS) {
      return memoryCache
    }
  }

  const storage = getStorage()
  if (!storage) return null

  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return null

    const snapshot = JSON.parse(raw)
    if (
      !snapshot ||
      snapshot.version !== VIEW_MODEL_VERSION ||
      !snapshot.cartId ||
      !snapshot.cartFingerprint ||
      !snapshot.viewModel?.cartItems?.length ||
      !snapshot.shopperKey ||
      !snapshot.locale
    ) {
      removeViewModelStorage(storage)
      return null
    }

    if (cartId && snapshot.cartId !== cartId) {
      return null
    }

    if (cartFingerprint && snapshot.cartFingerprint !== cartFingerprint) {
      return null
    }

    if (buildKey && snapshot.buildKey !== buildKey) {
      return null
    }

    if (snapshot.shopperKey !== currentShopperKey) {
      return null
    }

    if (snapshot.locale !== resolvedLocale) {
      return null
    }

    const age = Date.now() - snapshot.savedAt
    if (typeof snapshot.savedAt !== "number" || age < 0 || age > MAX_AGE_MS) {
      removeViewModelStorage(storage)
      return null
    }

    setMemoryCache({
      cartId: snapshot.cartId,
      cartVersion: snapshot.cartVersion || 0,
      shopperKey: snapshot.shopperKey,
      locale: snapshot.locale,
      cartFingerprint: snapshot.cartFingerprint,
      viewModel: snapshot.viewModel,
      buildKey: snapshot.buildKey || "",
    })

    return {
      cartId: snapshot.cartId,
      cartVersion: snapshot.cartVersion || 0,
      shopperKey: snapshot.shopperKey,
      locale: snapshot.locale,
      cartFingerprint: snapshot.cartFingerprint,
      buildKey: snapshot.buildKey || "",
      viewModel: snapshot.viewModel,
      savedAt: snapshot.savedAt,
    }
  } catch (err) {
    console.warn("Failed to read cart landing view-model:", err)
    removeViewModelStorage(storage)
    return null
  }
}

/**
 * Sync read for Landing's first client render (memory, then localStorage).
 * Always identity-gated via current shopper key, and optionally cart identity.
 * @param {Object} [options] Cart identity and locale.
 * @return {Object} Initial paint state.
 */
export const getInitialCartLandingPaint = ({
  cartId = "",
  locale = "",
  cartFingerprint = "",
  disableCache = false,
} = {}) => {
  if (disableCache) {
    return {
      cartData: {},
      cartDataStatus: false,
      pendingServerValidation: false,
      cachedCartId: "",
      cachedCartVersion: 0,
      cachedCartFingerprint: "",
      cachedBuildKey: "",
    }
  }

  const cached = readCartLandingViewModel({
    cartId,
    locale,
    cartFingerprint,
  })
  if (cached?.viewModel?.cartItems?.length) {
    return {
      cartData: cached.viewModel,
      cartDataStatus: true,
      pendingServerValidation: true,
      cachedCartId: cached.cartId,
      cachedCartVersion: cached.cartVersion,
      cachedCartFingerprint: cached.cartFingerprint,
      cachedBuildKey: cached.buildKey || "",
    }
  }

  return {
    cartData: {},
    cartDataStatus: false,
    pendingServerValidation: false,
    cachedCartId: "",
    cachedCartVersion: 0,
    cachedCartFingerprint: "",
    cachedBuildKey: "",
  }
}
